// Type-to-filter select (combobox). Drop-in replacement for a native <select>
// when the option list is long: the user types to narrow instead of scrolling.
import { useState, useRef, useEffect } from "react";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils/cn";

export interface SelectOption {
  value: string;
  label: string;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  /** Label shown for the empty ("all") option; also clears the selection. */
  allowClear?: boolean;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = "— Chọn —",
  className,
  allowClear = true,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  const selected = options.find((o) => o.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const q = query.trim().toLowerCase();
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q)) : options;

  function pick(v: string) {
    onChange(v);
    setOpen(false);
    setQuery("");
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex h-9 w-full items-center gap-1 rounded-md border border-input bg-background px-3 text-sm"
      >
        <span className={cn("flex-1 truncate text-left", !selected && "text-muted-foreground")}>
          {selected ? selected.label : placeholder}
        </span>
        {allowClear && selected ? (
          <X
            size={14}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            onClick={(e) => { e.stopPropagation(); pick(""); }}
          />
        ) : (
          <ChevronDown size={14} className="shrink-0 text-muted-foreground" />
        )}
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full rounded-md border bg-card shadow-lg">
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Gõ để tìm…"
            className="w-full border-b bg-transparent px-3 py-2 text-sm focus:outline-none"
          />
          <ul className="max-h-56 overflow-auto py-1 text-sm">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-muted-foreground">Không có kết quả</li>
            )}
            {filtered.map((o) => (
              <li
                key={o.value}
                onMouseDown={(e) => { e.preventDefault(); pick(o.value); }}
                className={cn(
                  "cursor-pointer px-3 py-1.5 hover:bg-accent",
                  o.value === value && "bg-accent font-medium",
                )}
              >
                {o.label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
